import { Box, Button, ButtonGroup, Chip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import { useMemo, useState } from 'react';
import { InheritanceDiagramNode } from './buildOrganizationInheritanceDiagram';
import {
  collectAllNodeIds,
  collectExpandableNodeIds,
  defaultExpandedIds,
  DiagramLeafMode,
} from './layoutOrganizationTree';
import { OrganizationInheritanceTreeCanvas } from './OrganizationInheritanceTreeCanvas';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(0.5),
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  declaredChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  inheritedChip: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(92, 124, 250, 0.22)' : 'rgba(25, 118, 210, 0.14)',
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.text.primary,
  },
  overrideChip: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  noneLabel: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  leafModeToggle: {
    marginLeft: 'auto',
  },
}));

function MembersLegend() {
  const classes = useStyles();
  return (
    <Box className={classes.legend}>
      <Chip
        size="small"
        label="Manager"
        style={{
          backgroundColor: 'rgba(212, 160, 23, 0.18)',
          border: '1px solid #d4a017',
        }}
      />
      <Chip
        size="small"
        label="Member"
        style={{
          backgroundColor: 'rgba(79, 111, 98, 0.12)',
          border: '1px solid #9cb8ab',
        }}
      />
    </Box>
  );
}

function BoardModeLegend() {
  const classes = useStyles();
  return (
    <Box className={classes.legend}>
      <Chip size="small" label="Differs from parent" className={classes.declaredChip} />
      <Chip size="small" label="Inherited" className={classes.inheritedChip} />
      <Chip size="small" label="Repo override" className={classes.overrideChip} />
      <Typography variant="caption" className={classes.noneLabel}>
        No board
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
        Same board as parent uses inherited styling · dark blue only when keys differ
      </Typography>
    </Box>
  );
}

function useExpandedState(
  officialRoot: InheritanceDiagramNode | undefined,
  unofficialRoot: InheritanceDiagramNode | undefined,
  leafMode: DiagramLeafMode,
) {
  const initialExpanded = useMemo(() => {
    const ids = new Set<string>();
    if (officialRoot) {
      defaultExpandedIds(officialRoot, leafMode).forEach(id => ids.add(id));
    }
    return ids;
  }, [officialRoot, leafMode]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(initialExpanded);

  const toggle = (id: string) => {
    setExpandedIds(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAllOfficial = () => {
    if (!officialRoot) {
      return;
    }
    setExpandedIds(current => {
      const next = new Set(current);
      collectExpandableNodeIds(officialRoot, leafMode).forEach(id => next.add(id));
      return next;
    });
  };

  const collapseToOrgRoot = () => {
    if (!officialRoot) {
      return;
    }
    setExpandedIds(current => {
      const next = new Set(current);
      collectAllNodeIds(officialRoot).forEach(id => next.delete(id));
      defaultExpandedIds(officialRoot, leafMode).forEach(id => next.add(id));
      return next;
    });
  };

  const expandUnofficial = () => {
    if (!unofficialRoot) {
      return;
    }
    setExpandedIds(current => {
      const next = new Set(current);
      collectExpandableNodeIds(unofficialRoot, leafMode).forEach(id => next.add(id));
      next.add(unofficialRoot.id);
      return next;
    });
  };

  return {
    expandedIds,
    toggle,
    expandAllOfficial,
    collapseToOrgRoot,
    expandUnofficial,
  };
}

const LEAF_MODE_LABELS: Record<DiagramLeafMode, string> = {
  none: 'Teams only',
  members: 'Members',
  repos: 'Repos',
};

function expandAllLabel(leafMode: DiagramLeafMode): string {
  if (leafMode === 'members') {
    return 'Expand all teams & members';
  }
  if (leafMode === 'repos') {
    return 'Expand all teams & repos';
  }
  return 'Expand all teams';
}

function officialOrgDescription(leafMode: DiagramLeafMode): string {
  if (leafMode === 'members') {
    return 'Expand teams to see who belongs to each group. Membership comes from User memberOf and group spec.members in the catalog.';
  }
  if (leafMode === 'repos') {
    return 'Expand teams to see owned repos and Jira board inheritance on the right.';
  }
  return 'Team and department structure only. Switch to Members or Repos to drill down.';
}

export function OrganizationInheritanceDiagram({
  officialRoot,
  unofficialRoot,
  onCatalogNavigate,
  canvasHeight,
}: {
  officialRoot?: InheritanceDiagramNode;
  unofficialRoot?: InheritanceDiagramNode;
  onCatalogNavigate?: (path: string) => void;
  canvasHeight?: number | string;
}) {
  const classes = useStyles();
  // The unofficial ("Other owners") graph only renders in repos mode, so when
  // there is no official root default to repos; otherwise the user would see
  // controls and legends but an empty diagram until switching modes.
  const [leafMode, setLeafMode] = useState<DiagramLeafMode>(
    officialRoot ? 'members' : 'repos',
  );
  const {
    expandedIds,
    toggle,
    expandAllOfficial,
    collapseToOrgRoot,
    expandUnofficial,
  } = useExpandedState(officialRoot, unofficialRoot, leafMode);

  if (!officialRoot && !unofficialRoot) {
    return (
      <Typography variant="body2" color="textSecondary">
        No organization data is available for the diagram.
      </Typography>
    );
  }

  return (
    <>
      {leafMode === 'repos' ? <BoardModeLegend /> : null}
      {leafMode === 'members' ? <MembersLegend /> : null}
      <Box className={classes.controls}>
        {officialRoot ? (
          <>
            <Button size="small" variant="outlined" onClick={expandAllOfficial}>
              {expandAllLabel(leafMode)}
            </Button>
            <Button size="small" variant="outlined" onClick={collapseToOrgRoot}>
              Collapse to Invisible Tech
            </Button>
          </>
        ) : null}
        {unofficialRoot && leafMode === 'repos' ? (
          <Button size="small" variant="outlined" onClick={expandUnofficial}>
            Expand other owners
          </Button>
        ) : null}
        <ButtonGroup className={classes.leafModeToggle} size="small">
          {(['none', 'members', 'repos'] as DiagramLeafMode[]).map(mode => (
            <Button
              key={mode}
              variant={leafMode === mode ? 'contained' : 'outlined'}
              color={leafMode === mode ? 'primary' : 'default'}
              onClick={() => setLeafMode(mode)}
            >
              {LEAF_MODE_LABELS[mode]}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {officialRoot ? (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Official invisible org
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            {officialOrgDescription(leafMode)}
          </Typography>
          <OrganizationInheritanceTreeCanvas
            root={officialRoot}
            expandedIds={expandedIds}
            onToggle={toggle}
            onCatalogNavigate={onCatalogNavigate}
            canvasHeight={canvasHeight}
            leafMode={leafMode}
          />
        </Box>
      ) : null}

      {unofficialRoot && leafMode === 'repos' ? (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Other owners
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Repos not owned by a team in the official invisibleorg catalog tree.
          </Typography>
          <OrganizationInheritanceTreeCanvas
            root={unofficialRoot}
            expandedIds={expandedIds}
            onToggle={toggle}
            onCatalogNavigate={onCatalogNavigate}
            canvasHeight={canvasHeight}
            leafMode={leafMode}
          />
        </Box>
      ) : null}
    </>
  );
}

export function OrganizationDiagramButton({
  onClick,
}: {
  onClick: () => void;
}) {
  return (
    <Button color="primary" variant="outlined" startIcon={<AccountTreeIcon />} onClick={onClick}>
      Org graph
    </Button>
  );
}
